import React, {
  memo,
  useEffect,
  useRef,
  useLayoutEffect,
  useCallback,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import actions from '@flux/action';
import ManageMHeader from './header';
import ManageMForm from './manageMForm';
import { FORM_KEYS } from './manageMForm/config/form.keys';
import useIdleTimer from '@hooks/useIdleTimer';
import useConcurrentEditLock from '@hooks/useConcurrentEditLock';
import alert from '@components/alert';
import { NETWORK_ERROR, projectRole } from '@model';
import { useHistory } from 'react-router-dom';
import useProjectId from '@hooks/useProjectId';
import { ErrorBackTo } from '@components/error';
import { navigationTilesAddModalConfig } from '@model/settings/addModalConfig';
import { addModuleConfig } from '@modules/projects/integrationHub/manageModules/manageMForm/config/add.config';

function ManageModules({ location }) {
  const valueInProps = useSelector(
    ({ projects: { projectDetail }, common: { projectCompanyInfo } }) => {
      const tabs = projectDetail.tabs || [];
      return {
        integrationNavigatorName:
          projectCompanyInfo?.integrationNavigatorName ||
          FORM_KEYS.NAVI_NAME.label,
        tabs,
        currentTab: projectDetail.currentTab || tabs[0]?.id || 'Home',
        integrationModuleData: projectDetail.integrationModuleData,
        groupInfo: projectDetail.groupInfo,
        displayMode: projectDetail.originIntegrationData?.displayMode
          ? projectDetail.originIntegrationData.displayMode
          : 'List',
      };
    },
  );
  const userInfo = useSelector(({ login }) => login.userInfo);
  const projectCompanyInfo = useSelector(
    ({ common }) => common.projectCompanyInfo,
  );
  const history = useHistory();
  const projectId = useProjectId();
  const dispatch = useDispatch();
  const extendAbortController = useRef(null);
  const { releaseLock, releaseLockBeforeUnload, sendQuery } =
    useConcurrentEditLock({ auto: false });

  useLayoutEffect(() => {
    function onBeforeunload(e) {
      e.preventDefault();
      if (window.location.pathname.includes('manageTiles')) {
        releaseLockBeforeUnload({
          moduleIdentifier: 'navigator',
          userEmail: userInfo.email,
          userName: userInfo.name,
        });
      }
    }

    if (!location?.state?.fromProjectHome) {
      const isContributor =
        userInfo.isInternal &&
        projectCompanyInfo?.currentUserPermission === projectRole.Contributor;
      history.replace(
        isContributor
          ? `/project-detail/${projectId}`
          : `/error?backTo=${ErrorBackTo.projectDetail}&projectId=${projectId}`,
      );
      // dispatch(
      //   commonActions.showBanner({
      //     status: 'warning',
      //     content: 'Sorry. You don’t have the permission to open the page.',
      //     closeable: true,
      //   }),
      // );
    } else {
      history.push({ ...location, state: { fromProjectHome: false } });
      window.addEventListener('beforeunload', onBeforeunload);
    }
    // window.removeEventListener('beforeunload', onBeforeunload);
  }, []);

  useEffect(() => {
    if (valueInProps.integrationModuleData) {
      return;
    }

    dispatch(actions.fetchProjectDetails(projectId));
  }, [valueInProps.integrationModuleData]);

  const { extendValidity } = useConcurrentEditLock({ auto: false });

  const goToProjectDetail = () => {
    if (projectId) {
      releaseLock({
        moduleIdentifier: 'navigator',
        userEmail: userInfo.email,
        userName: userInfo.name,
      }).finally(() => {
        history.replace(`/project-detail/${projectId}`);
      });
    }
  };

  const editTimeout = () => {
    alert.warning({
      className: 'edit-lock-warning',
      content:
        'Hi there! Your editing session has been timed out after 20 minutes of inactivity.',
      onOk: goToProjectDetail,
      onCancel: goToProjectDetail,
    });
    if (extendAbortController.current) {
      extendAbortController.current.abort();
    }
    releaseLock({
      moduleIdentifier: 'navigator',
      userEmail: userInfo.email,
      userName: userInfo.name,
    });
  };

  const onTimerRefresh = () => {
    extendAbortController.current = new AbortController();
    extendValidity(
      {
        moduleIdentifier: 'navigator',
        userEmail: userInfo?.email,
      },
      {
        signal: extendAbortController?.current.signal,
      },
    ).then((res) => {
      if (res && !res?.extendSuccessful) {
        alert.error({
          className: 'edit-lock-warning',
          content: res?.message || NETWORK_ERROR.INTERNAL_SERVER_ERROR,
          onOk: goToProjectDetail,
          onCancel: goToProjectDetail,
        });
      }
    });
  };

  useEffect(
    () => () => {
      // if (extendAbortController.current) {
      //   extendAbortController.current.abort();
      // }
      if (location?.state?.fromProjectHome) {
        releaseLock({
          moduleIdentifier: 'navigator',
          userEmail: userInfo.email,
          userName: userInfo.name,
        });
      }
    },
    [],
  );

  useIdleTimer({
    selector: '.manage-module-wrapper',
    onTimeout: editTimeout,
    onRefresh: onTimerRefresh,
    events: ['click', 'input'],
    // timeout: 30 * 1000,
  });

  const saveData = useCallback(
    async (data) => {
      const { lockInfo } = (await sendQuery()) || {};
      if (
        lockInfo.length &&
        lockInfo.some(
          (i) =>
            i.moduleIdentifier === 'navigator' &&
            i.userEmail === userInfo.email &&
            i.lockStatus,
        )
      ) {
        return await dispatch(actions.saveIntegrations(data));
      }
    },
    [sendQuery, dispatch, userInfo.email],
  );

  const afterSaveData = useCallback(async () => {
    await dispatch(actions.fetchAcceleratingTransition(projectId));
    history.replace(`/project-detail/${projectId}`);
  }, [dispatch, projectId, history]);

  return (
    <div className="page-wrapper manage-module-wrapper">
      <ManageMHeader />
      <ManageMForm
        key="manage-integration"
        {...valueInProps}
        showSpn
        pendoField="ManageTiles"
        saveData={saveData}
        afterSaveData={afterSaveData}
        configs={{ addModuleConfig: navigationTilesAddModalConfig }}
        ratioNeedFullSize
      />
    </div>
  );
}

ManageModules.propTypes = {};

export default memo(ManageModules);
